import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, inject, provideBrowserGlobalErrorListeners, provideEnvironmentInitializer } from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';

import { routes } from './app.routes';
import { GlobalTooltip } from './core/a11y/global-tooltip';
import { RouteFocus } from './core/a11y/route-focus';
import { ChunkLoadErrorHandler } from './core/errors/chunk-load-error.handler';
import { connectionErrorInterceptor } from './core/http/connection-error.interceptor';
import { loadingInterceptor } from './core/http/loading.interceptor';
import { mockInterceptors } from './core/http/mock-backend/mock-interceptors';
import { sessionExpiredInterceptor } from './core/http/session-expired.interceptor';
import { FlowScroll } from './core/navigation/flow-scroll';
import { animateFlowStep } from './core/navigation/flow-transition';
import { SessionLifecycle } from './features/access/session-lifecycle';
import { environment } from '@env/environment.local';
import { ENVIRONMENT } from '@env/environment.model';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Sin soporte de la API, el router simplemente cambia de página sin animar.
    provideRouter(routes, withViewTransitions({ skipInitialTransition: true, onViewTransitionCreated: animateFlowStep })),
    // Foco al <h1> en cada navegación (plan 6.10): el servicio escucha al router desde el arranque.
    provideEnvironmentInitializer(() => inject(RouteFocus)),
    // Deja a la vista el contenido al cambiar de paso, haya animación o no.
    provideEnvironmentInitializer(() => inject(FlowScroll)),
    // Cierre por inactividad y polling del estado del token: escucha la sesión desde el arranque y vigila solo con una activa.
    provideEnvironmentInitializer(() => inject(SessionLifecycle)),
    // Tooltip de data-tooltip en cualquier elemento visible, también dentro de un dialog abierto.
    provideEnvironmentInitializer(() => inject(GlobalTooltip)),
    // Recarga una vez cuando un chunk lazy ya no existe (deploy nuevo con la pestaña abierta).
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    // El mock va último: los demás interceptores (overlay, aviso de conexión) actúan igual que contra el backend real.
    // Solo existe en local: en los entornos desplegados `mockInterceptors` es un array vacío (fileReplacements).
    provideHttpClient(
      withInterceptors([
        loadingInterceptor,
        sessionExpiredInterceptor,
        connectionErrorInterceptor,
        ...mockInterceptors,
      ]),
    ),
    { provide: ENVIRONMENT, useValue: environment },
  ],
};
